import { isUndefined } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import {
  DispositiveProvisionTemplateKind,
  DispositiveProvisionTransferTaxKind,
} from '@/types/schema';

import {
  DispositiveProvisionsFormPaths,
  DispositiveProvisionsFormRecipient,
  DispositiveProvisionsFormShape,
} from '../../DispositiveProvisionsForm.types';
import { isLastRecipientWithAnythingLeftOver } from './useLastRecipientWithAnythingLeftOverIndex';

/**
 * NOTE: This hook is only used to set the DP Forms V1 bearsTax default value
 * based on the recipient's transfer tax kind.
 *
 * @description This hook will set the default bearsTax value for a recipient based on the recipient's transfer tax kind.
 *
 * By default:
 * - Non-taxable (charitable, marital deduction) dispositions don't bear tax
 * - Taxable dispositions do bear tax
 */
export function useDefaultBearsTax(
  _recipient: DispositiveProvisionsFormRecipient | undefined,
  recipientIndex: number,
  templateType?: DispositiveProvisionTemplateKind
) {
  const {
    setValue,
    getFieldState,
    control,
    formState: { isLoading, defaultValues },
  } = useFormContext<DispositiveProvisionsFormShape>();

  const bearsTaxFieldName =
    `dispositiveProvisions.recipients.${recipientIndex}.bearsTax` as const satisfies DispositiveProvisionsFormPaths;
  const fieldDefaultValue =
    defaultValues?.dispositiveProvisions?.recipients?.[recipientIndex]
      ?.bearsTax;

  const recipientBearsTax = useWatch({
    control,
    name: bearsTaxFieldName,
  });

  const transferTaxKind = useWatch({
    control,
    name: `dispositiveProvisions.recipients.${recipientIndex}.transferTaxKind` as const satisfies DispositiveProvisionsFormPaths,
  });

  useEffect(() => {
    if (templateType) return;

    const { isDirty } = getFieldState(bearsTaxFieldName);

    if (isDirty || fieldDefaultValue !== undefined || isLoading) return;

    if (!transferTaxKind) return;

    // if the recipientBearsTax field is already set, and if the template doesn't
    // allow overriding it, skip
    if (recipientBearsTax !== undefined) {
      return;
    }

    const defaultBearsTax = !isNonTaxable(transferTaxKind);

    setValue(bearsTaxFieldName, defaultBearsTax, {
      shouldDirty: false,
    });
  }, [
    fieldDefaultValue,
    getFieldState,
    isLoading,
    recipientBearsTax,
    setValue,
    bearsTaxFieldName,
    transferTaxKind,
    templateType,
  ]);
}

const TEMPLATES_WITH_SPECIAL_HANDLING = [
  DispositiveProvisionTemplateKind.AbTrust,
  DispositiveProvisionTemplateKind.AbcTrust,
  DispositiveProvisionTemplateKind.MaritalTrust,
  DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse,
];

export function useTemplateRecipientBearsTax(
  recipientIndex: number,
  templateType?: DispositiveProvisionTemplateKind
): void {
  const {
    setValue,
    getFieldState,
    control,
    formState: { isLoading },
  } = useFormContext<DispositiveProvisionsFormShape>();

  const [transferTaxKind, recipients] = useWatch({
    control,
    name: [
      `dispositiveProvisions.recipients.${recipientIndex}.transferTaxKind` as const satisfies DispositiveProvisionsFormPaths,
      `dispositiveProvisions.recipients` as const satisfies DispositiveProvisionsFormPaths,
    ],
  });

  const isValidLastRecipient = useMemo<boolean>(() => {
    return isLastRecipientWithAnythingLeftOver(recipients, recipientIndex);
  }, [recipients, recipientIndex]);

  const currentRecipient = useMemo<
    DispositiveProvisionsFormRecipient | undefined
  >(() => recipients?.[recipientIndex], [recipients, recipientIndex]);

  const bearsTaxFieldName =
    `dispositiveProvisions.recipients.${recipientIndex}.bearsTax` as const satisfies DispositiveProvisionsFormPaths;

  useEffect(() => {
    // If no template is provided or it's not one that requires special handling, exit early
    if (
      !templateType ||
      !TEMPLATES_WITH_SPECIAL_HANDLING.includes(templateType)
    ) {
      return;
    }

    const { isDirty } = getFieldState(bearsTaxFieldName);

    // if the field is already dirty, or if the form is loading, or if the
    // transfer tax kind is not set, skip
    if (isDirty || isLoading || !transferTaxKind) return;

    if (!isValidLastRecipient) return;

    if (!currentRecipient) return;

    if (!isUndefined(currentRecipient.bearsTax)) return;

    if (isNonTaxable(transferTaxKind, templateType)) return;

    setValue(bearsTaxFieldName, true, {
      shouldDirty: false,
    });
  }, [
    bearsTaxFieldName,
    currentRecipient,
    getFieldState,
    isValidLastRecipient,
    isLoading,
    recipientIndex,
    setValue,
    templateType,
    transferTaxKind,
  ]);
}

export function isNonTaxable(
  transferTaxKind: DispositiveProvisionTransferTaxKind | '',
  templateType?: DispositiveProvisionTemplateKind
): boolean {
  if (!transferTaxKind) return false;

  // for templates, also default to taxable if the transfer kind includes marital deduction
  if (templateType) {
    return transferTaxKind === DispositiveProvisionTransferTaxKind.Charitable;
  }

  return [
    DispositiveProvisionTransferTaxKind.Charitable,
    DispositiveProvisionTransferTaxKind.SpouseMaritalExclusion,
    DispositiveProvisionTransferTaxKind.SpouseFederalCreditShelterStateMaritalExclusion,
  ].includes(transferTaxKind as DispositiveProvisionTransferTaxKind);
}
